/*
 * <olivida-plan-picker> — subscription vs. one-time purchase choice on the product page.
 * Markup lives in snippets/olivida-plan-picker.liquid.
 *
 * Owns the selection state: which card is active, which delivery rhythm, the
 * subscription card's price / discount chip for that rhythm, and the `selling_plan`
 * hidden inputs that carry the choice into the product form(s).
 *
 * A product with a usage plan (see olivida-usage-group) starts with the rhythm
 * list folded behind a toggle and a delivery card describing that plan; the
 * card only shows while the usage plan is the chosen rhythm.
 */
class OlividaPlanPicker extends HTMLElement {
  connectedCallback() {
    // All listeners are registered with this signal so a section re-render
    // (disconnect + reconnect) never leaves stale handlers behind.
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.inputs = this.querySelectorAll('[data-abo-input]');
    this.subscriptionCard = this.querySelector('[data-abo-kaart="abonnement"]');
    this.oneTimeCard = this.querySelector('[data-abo-kaart="eenmalig"]');
    this.planPrice = this.querySelector('[data-abo-prijs]');
    this.planComparePrice = this.querySelector('[data-abo-prijs-oud]');
    this.planLaterPrice = this.querySelector('[data-abo-prijs-later]');
    this.discountChip = this.querySelector('[data-abo-kortingchip]');
    this.frequencies = Array.from(this.querySelectorAll('[data-abo-plan]'));
    this.optionsToggle = this.querySelector('[data-abo-toggle]');
    this.options = this.querySelector('[data-abo-options]');
    this.deliveryCard = this.querySelector('[data-abo-delivery]');
    if (!this.subscriptionCard || !this.oneTimeCard || !this.frequencies.length) return;

    this.subscriptionRow = this.subscriptionCard.querySelector('.olvd-abo__rij');
    this.oneTimeRow = this.oneTimeCard.querySelector('.olvd-abo__rij');

    this.lastTrackedSelection = this.selectionKey();

    this.subscriptionRow.addEventListener(
      'click',
      () => {
        this.select(true);
        this.trackSelection('card');
      },
      { signal }
    );
    this.oneTimeRow.addEventListener(
      'click',
      () => {
        this.select(false);
        this.trackSelection('card');
      },
      { signal }
    );
    this.frequencies.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          this.selectFrequency(button);
          this.trackSelection('frequency');
        },
        { signal }
      );
    });
    if (this.optionsToggle && this.options) {
      this.optionsToggle.addEventListener('click', () => this.toggleOptions(), { signal });
    }
  }

  // Unfolds or folds the rhythm list behind "I use it less often, or want to
  // pick my own rhythm".
  toggleOptions() {
    const open = this.optionsToggle.getAttribute('aria-expanded') !== 'true';
    this.optionsToggle.setAttribute('aria-expanded', String(open));
    this.options.hidden = !open;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  activeFrequency() {
    return this.frequencies.find((button) => button.classList.contains('is-actief')) || this.frequencies[0];
  }

  // Switch between the subscription and one-time cards and write the chosen plan
  // (or nothing) into every product form this picker feeds.
  select(subscription) {
    this.subscriptionCard.classList.toggle('is-actief', subscription);
    this.oneTimeCard.classList.toggle('is-actief', !subscription);
    this.subscriptionRow.setAttribute('aria-pressed', String(subscription));
    this.oneTimeRow.setAttribute('aria-pressed', String(!subscription));

    const planId = subscription ? this.activeFrequency().dataset.aboPlan : '';
    this.inputs.forEach((input) => {
      input.value = planId;
    });
  }

  selectFrequency(button) {
    this.frequencies.forEach((other) => {
      other.classList.toggle('is-actief', other === button);
      other.setAttribute('aria-checked', String(other === button));
    });

    // innerHTML, not textContent: money_format may contain HTML entities
    // (e.g. &euro;) that would otherwise show up literally.
    const discount = (button.dataset.aboPlanKorting || '').trim();
    if (this.planPrice) this.planPrice.innerHTML = button.dataset.aboPlanPrijs;
    if (this.discountChip) {
      this.discountChip.innerHTML = discount;
      this.discountChip.hidden = !discount;
    }
    // The struck price only belongs to a plan that is cheaper than the
    // reference (compare-at) price; the chip text is empty otherwise.
    if (this.planComparePrice) this.planComparePrice.hidden = !discount;
    // "Then €X per delivery": only a plan whose first delivery is priced
    // differently from the ones after it carries this text.
    if (this.planLaterPrice) {
      const later = (button.dataset.aboPlanLater || '').trim();
      this.planLaterPrice.innerHTML = later;
      this.planLaterPrice.hidden = !later;
    }
    // The delivery card describes the usage plan; it would lie next to any
    // other rhythm, so it only shows while that plan is chosen.
    if (this.deliveryCard) this.deliveryCard.hidden = !button.hasAttribute('data-abo-plan-usage');
    this.select(true);
  }

  isSubscriptionSelected() {
    return this.subscriptionCard.classList.contains('is-actief');
  }

  // The chosen plan id, or 'one_time'. Starts from what the page rendered, so
  // only a real change by the visitor is tracked.
  selectionKey() {
    return this.isSubscriptionSelected() ? this.activeFrequency().dataset.aboPlan : 'one_time';
  }

  // PostHog "Plan Option Selected", from the raw data-* values the Liquid
  // snippets render next to the formatted ones (olivida-plan-option.liquid).
  // Plans are identified by selling_plan_id: the plan name is translated and
  // editable, so it is only sent as a label.
  trackSelection(interaction) {
    const selection = this.selectionKey();
    if (selection === this.lastTrackedSelection) return;
    this.lastTrackedSelection = selection;

    if (!window.posthog) return;

    const properties = {
      brand: this.dataset.brand,
      interaction: interaction,
      name: this.dataset.productName,
      product_id: this.dataset.productId,
      variant_id: this.dataset.variantId,
    };

    if (this.isSubscriptionSelected()) {
      const plan = this.activeFrequency();
      Object.assign(properties, {
        discount_pct: Number(plan.dataset.aboPlanKortingPct) || 0,
        is_popular: plan.hasAttribute('data-abo-plan-populair'),
        is_usage_plan: plan.hasAttribute('data-abo-plan-usage'),
        plan_name: plan.querySelector('.olvd-abo__freq-titel')?.textContent.trim(),
        price: Number(plan.dataset.aboPlanBedrag),
        purchase_type: 'subscription',
        selling_plan_id: plan.dataset.aboPlan,
      });
    } else {
      Object.assign(properties, {
        price: Number(this.dataset.eenmaligBedrag),
        purchase_type: 'one_time',
      });
    }

    posthog.capture('Plan Option Selected', properties);
  }
}

customElements.define('olivida-plan-picker', OlividaPlanPicker);
